import { Layout } from "@/components/layout";
import { useParams, Link } from "wouter";
import { useGetSession, getGetSessionQueryKey, useListSessionAnswers, getListSessionAnswersQueryKey, type AnswerRecord } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, XCircle, ArrowLeft, RotateCcw, GraduationCap, Trophy, AlertOctagon, AlertTriangle, Bookmark, BookmarkCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { getExamRuleForTrack, calcExamResult } from "@/lib/tc-exam-rules";
import { useLanguage } from "@/contexts/language";
import { useState } from "react";

const BOOKMARK_KEY = "cpl_saved_questions";

function loadBookmarks(): Set<number> {
  try {
    const raw = localStorage.getItem(BOOKMARK_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveBookmarks(set: Set<number>) {
  localStorage.setItem(BOOKMARK_KEY, JSON.stringify([...set]));
}
function debriefAnswerCorrect(ans: AnswerRecord): boolean {
  const selectedAnswer = Math.trunc(Number(ans.selectedOptionIndex));
  const correctAnswer = Math.trunc(Number(ans.question.correctOptionIndex));
  if (!Number.isFinite(selectedAnswer) || !Number.isFinite(correctAnswer)) return false;
  return selectedAnswer === correctAnswer;
}

export default function Review() {
  const { t, language } = useLanguage();
  const r = t.review;
  const [bookmarked, setBookmarked] = useState<Set<number>>(loadBookmarks);
  const { sessionId } = useParams<{ sessionId: string }>();
  const id = parseInt(sessionId || "0", 10);

  const { data: session, isLoading: sessionLoading } = useGetSession(id, {
    query: { enabled: !!id, queryKey: getGetSessionQueryKey(id) }
  });

  const { data: answers, isLoading: answersLoading } = useListSessionAnswers(id, {
    query: { enabled: !!id, queryKey: getListSessionAnswersQueryKey(id) }
  });

  const isLoading = sessionLoading || answersLoading;

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-1/3" />
          <Skeleton className="h-48 w-full" />
          <div className="space-y-4">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!session) {
    return (
      <Layout>
        <div className="p-8 text-center text-destructive font-mono">{r.errNotFound}</div>
      </Layout>
    );
  }

  const isExamMode = session.mode === "exam";
  const examRule = getExamRuleForTrack(session.licenseTrack);

  // Per-section breakdown (exam mode only)
  const examResult = isExamMode && answers ? calcExamResult(answers, examRule) : null;

  const overallScore = examResult
  ? examResult.overallPercent
  : (session.totalQuestions > 0
      ? Math.round((session.correctCount / session.totalQuestions) * 100)
      : 0);

  // Practice mode: passing = 60%; Exam mode: passing = all sections AND overall ≥ 70%
  const practicePass = examResult
  ? examResult.overallPassed
  : overallScore >= 60;

  // For exam result stamp
  const examFullPass = examResult?.fullPass ?? false;
  const examPartialFail = examResult ? (examResult.overallPassed && !examResult.fullPass) : false;

  // What label to show on the stamp
  const examStampColor = examFullPass ? "border-primary text-primary bg-primary/10"
    : examPartialFail ? "border-yellow-500 text-yellow-400 bg-yellow-500/10"
    : "border-destructive text-destructive bg-destructive/10";
  const examBarColor = examFullPass ? "bg-primary" : examPartialFail ? "bg-yellow-500" : "bg-destructive";

  const toggleBookmark = (questionId: number) => {
    setBookmarked(prev => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      saveBookmarks(next);
      return next;
    });
  };

  return (
    <Layout>
      <div className="study-layout space-y-8 animate-in fade-in duration-500">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link href="/">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {isExamMode ? r.examDebrief : r.debriefing}
            </h1>
            <p className="text-muted-foreground font-mono text-sm mt-1">
              SESSION {session.id} · {isExamMode ? "TC CPL WRITTEN EXAM" : `MODE: ${session.mode.toUpperCase()}`}
            </p>
          </div>
        </div>

        {/* Exam result card */}
        {isExamMode && examResult ? (
          <Card className={cn("border-2 overflow-hidden", examFullPass ? "border-primary/60" : examPartialFail ? "border-yellow-500/60" : "border-destructive/60")}>
            <div className={cn("h-2 w-full", examBarColor)} />
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center gap-8">
                {/* Stamp */}
                <div className={cn(
                  "shrink-0 flex flex-col items-center justify-center w-40 h-40 rounded-full border-4 font-mono font-black tracking-widest text-center",
                  examStampColor
                )}>
                  {examFullPass ? (
                    <><Trophy className="w-8 h-8 mb-1" /><span className="text-3xl">{r.pass}</span></>
                  ) : examPartialFail ? (
                    <><AlertTriangle className="w-7 h-7 mb-1" /><span className="text-xl leading-tight" style={{whiteSpace:"pre-line"}}>{r.partialFail}</span></>
                  ) : (
                    <><AlertOctagon className="w-8 h-8 mb-1" /><span className="text-3xl">{r.fail}</span></>
                  )}
                </div>

                <div className="flex-1 space-y-4 w-full">
                  {/* Overall score */}
                  <div>
                    <div className={cn(
                      "text-5xl font-bold font-mono",
                      examFullPass ? "text-primary" : examPartialFail ? "text-yellow-400" : "text-destructive"
                    )}>
                      {overallScore}%
                    </div>
                    <div className="text-muted-foreground text-sm mt-1">
                      {r.correctOutOf.replace("{n}", String(session.correctCount)).replace("{total}", String(session.totalQuestions))}
                    </div>
                  </div>

                  {/* Partial fail explanation */}
                  {examPartialFail && (
                    <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-md text-xs text-yellow-400 font-mono">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>
                        {examResult.failedSections.length === 1
                          ? r.partialFailNoteOne
                              .replace("{score}", String(overallScore))
                              .replace(/{pass}/g, String(examRule.passPercent))
                              .replace("{cat}", examResult.failedSections[0].category)
                          : r.partialFailNoteMany
                              .replace("{score}", String(overallScore))
                              .replace(/{pass}/g, String(examRule.passPercent))
                              .replace("{n}", String(examResult.failedSections.length))}
                      </span>
                    </div>
                  )}

                  {/* Overall fail explanation */}
                  {!examFullPass && !examPartialFail && (
                    <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-xs text-destructive font-mono">
                      {examRule.passPercent - overallScore === 1
                        ? r.failNoteOne
                            .replace("{pass}", String(examRule.passPercent))
                            .replace("{score}", String(overallScore))
                            .replace("{short}", String(examRule.passPercent - overallScore))
                        : r.failNote
                            .replace("{pass}", String(examRule.passPercent))
                            .replace("{score}", String(overallScore))
                            .replace("{short}", String(examRule.passPercent - overallScore))
                            .replace("{s}", "s")}
                    </div>
                  )}

                  {/* Per-section breakdown */}
                  <div className="border-t border-border pt-4 space-y-2">
                    <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-3">{r.sectionBreakdown}</p>
                    <div className="space-y-2">
                      {examResult.sections.map((sec) => (
                        <div key={sec.category} className={cn(
                          "flex items-center justify-between px-4 py-2.5 rounded-md border text-sm font-mono",
                          sec.passed
                            ? "bg-primary/5 border-primary/20"
                            : "bg-destructive/5 border-destructive/20"
                        )}>
                          <div className="flex items-center gap-2">
                            {sec.passed
                              ? <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                              : <XCircle className="w-4 h-4 text-destructive shrink-0" />
                            }
                            <span className={sec.passed ? "text-foreground" : "text-destructive"}>{sec.category}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-muted-foreground text-xs">{sec.correct}/{sec.total}</span>
                            <span className={cn(
                              "font-bold w-12 text-right",
                              sec.passed ? "text-primary" : "text-destructive"
                            )}>
                              {sec.percent}%
                            </span>
                            <span className={cn(
                              "text-xs px-2 py-0.5 rounded-full border font-mono",
                              sec.passed
                                ? "bg-primary/10 border-primary/30 text-primary"
                                : "bg-destructive/10 border-destructive/30 text-destructive"
                            )}>
                              {sec.passed ? r.pass : r.fail}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground font-mono pt-1">
                      {r.minRequired.replace("{pass}", String(examRule.passPercent))}
                    </p>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Link href="/practice">
                      <Button size="lg">
                        <RotateCcw className="w-4 h-4 mr-2" />
                        {examFullPass ? r.flyAgain : r.goAround}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : !isExamMode ? (
          /* Practice mode result card */
          <Card className={cn("border-2 overflow-hidden", practicePass ? "border-primary/50" : "border-destructive/50")}>
            <div className={cn("h-2 w-full", practicePass ? "bg-primary" : "bg-destructive")} />
            <CardContent className="p-8 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">
                  {overallScore === 100 ? r.practiceLabel100
                    : overallScore >= 90 ? r.practiceLabel90
                    : overallScore >= 80 ? r.practiceLabel80
                    : overallScore >= 70 ? r.practiceLabel70
                    : r.practiceLabelFail}
                </h2>
                <p className="text-muted-foreground">
                  {r.answeredCorrectly.replace("{n}", String(session.correctCount)).replace("{total}", String(session.totalQuestions))}
                </p>
              </div>
              <div className="shrink-0 flex items-center gap-6">
                <div className="text-center">
                  <div className={cn("text-6xl font-bold font-mono", practicePass ? "text-primary" : "text-destructive")}>
                    {overallScore}%
                  </div>
                  <div className="text-sm font-medium text-muted-foreground uppercase tracking-widest mt-1">{r.flightScore}</div>
                </div>
              </div>
              <div className="shrink-0 border-l border-border pl-8">
                <Link href="/practice">
                  <Button size="lg" className="w-full">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {r.newFlight}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Answer log */}
        <div className="space-y-6">
          <h3 className="text-xl font-bold flex items-center gap-2">
            {isExamMode ? r.examReview : r.flightLog}
            <span className="text-sm font-normal text-muted-foreground font-mono bg-secondary px-2 py-1 rounded-md">
              {answers?.length || 0} {r.records}
            </span>
          </h3>

          <div className="space-y-4">
            {answers?.map((ans, idx) => {
              const selectedAnswer = Math.trunc(Number(ans.selectedOptionIndex));
              const correctAnswer = Math.trunc(Number(ans.question.correctOptionIndex));
              console.log({ selectedAnswer, correctAnswer });
              const showCorrect = debriefAnswerCorrect(ans);
              const isBookmarked = bookmarked.has(ans.question.id);
              const selIdx = Number.isFinite(selectedAnswer) ? selectedAnswer : 0;
              const corIdx = Number.isFinite(correctAnswer) ? correctAnswer : 0;
              return (
              <Card key={ans.id} className={cn("study-review-card border-l-4", showCorrect ? "border-l-primary" : "border-l-destructive")}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 pt-1">
                      {showCorrect
                        ? <CheckCircle2 className="w-6 h-6 text-primary" />
                        : <XCircle className="w-6 h-6 text-destructive" />
                      }
                    </div>
                    <div className="space-y-3 flex-1">
                      <div>
                        <span className="text-xs font-mono text-muted-foreground mr-2">Q{idx + 1}.</span>
                        <span className="text-xs font-mono text-muted-foreground mr-2 bg-secondary px-1.5 py-0.5 rounded">{ans.question.category}</span>
                        <span className="study-question-text font-medium text-lg leading-relaxed">
                          {(language === "fr" && ans.question.textFr) ? ans.question.textFr : ans.question.text}
                        </span>
                      </div>
                      <div>
                        <button
                          onClick={() => toggleBookmark(ans.question.id)}
                          title={isBookmarked ? r.unsaveQuestion : r.saveQuestion}
                          className={cn(
                            "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-mono transition-colors border",
                            isBookmarked
                              ? "bg-primary/20 text-primary border-primary/40 hover:bg-primary/10"
                              : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:border-foreground/30"
                          )}
                        >
                          {isBookmarked
                            ? <><BookmarkCheck className="w-3.5 h-3.5" /> {r.savedQuestion}</>
                            : <><Bookmark className="w-3.5 h-3.5" /> {r.saveQuestion}</>
                          }
                        </button>
                      </div>

                      <div className="bg-secondary/50 rounded-md p-4 space-y-2 text-sm">
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-muted-foreground w-20 shrink-0">{r.yourCall}</span>
                          <span className={cn("font-medium", showCorrect ? "text-primary" : "text-destructive")}>
                            {(language === "fr" && ans.question.optionsFr?.[selIdx])
                              ? ans.question.optionsFr[selIdx]
                              : ans.question.options[selIdx]}
                          </span>
                        </div>
                        {!showCorrect && (
                          <div className="flex items-start gap-2">
                            <span className="font-semibold text-muted-foreground w-20 shrink-0">{r.correctLabel}</span>
                            <span className="text-primary font-medium">
                              {(language === "fr" && ans.question.optionsFr?.[corIdx])
                                ? ans.question.optionsFr[corIdx]
                                : ans.question.options[corIdx]}
                            </span>
                          </div>
                        )}
                      </div>

                      {!showCorrect && ans.question.explanation && (
                        <div className="text-sm text-muted-foreground mt-4 pt-4 border-t border-border">
                          <strong className="text-foreground mr-2 font-mono">{r.explanation}</strong>
                          {(language === "fr" && ans.question.explanationFr) ? ans.question.explanationFr : ans.question.explanation}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}
